<template>
    <v-container v-if="/*!editObject &&*/ loading" class="pa-0"
        style="height: 100%; background-color: white; border-left: 1px solid var(--v-disabled-base); ">
        <v-skeleton-loader type="list-item" class="rounded-0"></v-skeleton-loader>
        <v-skeleton-loader type="list-item-avatar-three-line" class="my-4"></v-skeleton-loader>
        <v-skeleton-loader type="paragraph" style="opacity: 0.8; background-color: #f9f9f9;" class="px-4 py-3">
        </v-skeleton-loader>
        <v-skeleton-loader type="article" style="opacity: 0.5;"></v-skeleton-loader>
        <v-skeleton-loader type="article" style="opacity: 0.2;"></v-skeleton-loader>
    </v-container>

    <v-container v-else-if="error" class="pa-0">
        <EmptyPage v-if="error" :icon="'mdi-file-question'" :title="'Error'" :message="'An unknown error occurred.'">
            <template v-slot:actions>
                <v-card dark color="error" width="400" class="pa-3">
                    Error: {{ error }}
                </v-card>
            </template>
        </EmptyPage>
    </v-container>

    <v-container v-else-if="editObject" :key="editObject.Id" class="pa-0"
        style="height: 100%; overflow-y: auto; background-color: white; border-left: 1px solid var(--v-disabled-base); position: relative;">
        <v-progress-linear v-if="loading" indeterminate height="2"></v-progress-linear>
        <v-toolbar dense flat class="minitoolbar">
            <slot name="toolbar-content" :loading="loading">
                <v-btn icon small class="mr-2" @click="close()">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
                <h4 class="mt-1">{{ headerText }}</h4>
            </slot>
            <template v-if="!locked">
                <v-spacer></v-spacer>
                <v-btn v-if="!isNew && !loading" color="error" outlined :disabled="anyLoading" :loading="deleteLoading"
                    class="mr-2 px-2" small @click="remove">
                    <v-icon size="14" class="mr-1">$delete</v-icon>
                    {{ $t("servicesystem.common_objects.commonterms.delete") }}
                </v-btn>
                <v-btn v-if="!loading" :color="!isNew ? 'primary' : 'quaternary'" :disabled="anyLoading"
                    :loading="saveLoading" small :dark="!anyLoading" class="px-2 mr-n1" @click="save()">
                    <v-icon size="14" class="mr-1">$save</v-icon>
                    {{ !isNew ? $t("servicesystem.common_objects.commonterms.save") :
                        $t("servicesystem.common_objects.commonterms.add") }}
                </v-btn>
            </template>
        </v-toolbar>
        <v-progress-linear v-if="anyLoading" indeterminate
            style="position: absolute; left: 0; top: 48px; width: 100%; z-index: 2000;" height="1">
        </v-progress-linear>

        <v-row v-if="editObject.Article" no-gutters class="pa-3">
            <v-col class="pr-3" cols="auto">
                <ArticleImage :size="90" :relativePath="editObject.Article.ArticleImage"
                    :articleNumber="editObject.Article.ArticleNumber" :timeType="editObject.Type" />
            </v-col>
            <v-col cols="0" class="pl-3">
                <v-row no-gutters align="center">
                    <v-col cols="0">
                        <router-link class="primary--text minimalLink"
                            :to="{ name: 'ViewArticle', params: { articleId: editObject.Article.Id, articleNumber: editObject.Article.ArticleNumber } }">
                            <h4>{{ editObject.Article.ArticleNumber }}</h4>
                        </router-link>
                    </v-col>
                    <v-col v-if="timeTypeChip && !isPart" cols="auto">
                        <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                                <v-chip v-bind="attrs" v-on="on" small class="px-2" style="border-radius: 5px;"
                                    outlined>
                                    <v-icon size="14" class="mr-1">{{ timeTypeChip.icon }}</v-icon>
                                    {{ timeTypeChip.text }}
                                </v-chip>
                            </template>
                            <span
                                v-text="$t('servicesystem.common_objects.article.report_type_hint', { rkey: timeTypeChip.text })">
                            </span>
                        </v-tooltip>
                    </v-col>
                </v-row>
                <v-row no-gutters>
                    <h4>{{ editObject.Article.ArticleDescription }}</h4>
                </v-row>
                <v-divider class="my-2"></v-divider>
                <v-row no-gutters>
                    <v-col>{{ $t('servicesystem.common_objects.article.price') }}:</v-col>
                    <v-col class="text-right">{{ editObject.Article.ArticlePrice }},-</v-col>
                </v-row>
                <v-row v-if="editObject.Article.ArticlePriceIn" no-gutters>
                    <v-col>{{ $t('servicesystem.common_objects.article.pricein') }}:</v-col>
                    <v-col class="text-right">{{ editObject.Article.ArticlePriceIn }},-</v-col>
                </v-row>
            </v-col>
        </v-row>

        <TimeDurationEditor v-if="showHourEditor" v-model="timeControls" :key="timeControlkey"
            :disabled="locked || anyLoading">
        </TimeDurationEditor>

        <AutoArticleDescription :detail="editObject" :loading="anyLoading" :readonly="locked || anyLoading"
            @change="val => editObject.ArticleDescription = val">
        </AutoArticleDescription>

        <v-container class="pa-0">
            <v-row no-gutters class="mt-3 mb-2 px-3" align="end">
                <v-col>
                    <EmploymentSelect v-model="editObject.EmploymentId" singleline externaledit
                        :disabled="locked || anyLoading || $store.state.userInfo.UserTypeId != 1">
                    </EmploymentSelect>
                </v-col>
                <v-col cols="auto" v-if="!showHourEditor" class="pl-3">
                    <v-row no-gutters>
                        <v-icon size="18" class="mr-2 mt-1">mdi-calendar</v-icon>
                        <QDateField3 v-model="editObject.EmploymentTimeReportDate" style="width: 120px;" :min="null"
                            :max="null" :backgroundcolor="'#f9f9f9'" singleline externaledit
                            :disabled="locked || anyLoading">
                        </QDateField3>
                    </v-row>
                </v-col>
            </v-row>

            <PriceControl v-model="priceControls" :key="articleCountKey" :readonly="locked || anyLoading"
                class="mb-5 mt-5 px-3">
            </PriceControl>

            <StockSelector v-if="isNew && editObject.Article.IsStoreArticle" v-model="stockControls"
                :StoreItems="editObject.Article.StoreItems" :disabled="locked || anyLoading">
            </StockSelector>
        </v-container>
    </v-container>
</template>


<script>
import ClientAPI from "@/api/ClientAPIMixin";
import EmploymentSelect from "@/components/Inputs/EntityDropdowns/EmploymentSelect2.vue";
import TimeDurationEditor from "./TimeDurationEditor.vue";
import TimeTypeConstants from "@/helpers/Constants/TimeTypeConstants.js";
import ArticleImage from "@/views/pim/articles/ArticleImage.vue";
import StockSelector from "./StockSelector.vue";
import QDateField3 from "@/components/Inputs/QDateField3.vue";
import AutoArticleDescription from "./AutoArticleDescription.vue";
import PriceControl from "./PriceControl.vue";
import moment from "moment";
import EmptyPage from "@/components/Sections/EmptyPage.vue";

export default {
    name: "OrderDetailForm",
    props: {
        createFunc: Function,
        updateFunc: Function,
        deleteFunc: Function,
    },
    components: {
        EmploymentSelect,
        TimeDurationEditor,
        ArticleImage,
        StockSelector,
        QDateField3,
        AutoArticleDescription,
        PriceControl,
        EmptyPage,
    },

    data() {
        return {
            showDatePicker: false,
            loading: true,
            error: null,
            saveLoading: false,
            deleteLoading: false,
            articleCountKey: 1,
            timeControlkey: 1,
            editObject: null,
            fetchIteration: 0,
        };
    },

    mounted() {
        this.load();
    },

    methods: {
        close() {
            //console.log("CLOSE");
            this.$router.push({
                name: "ViewJobOrder",
                params: { filter: this.$route.params.filter, jobId: this.$route.params.jobId }
            });
        },

        async load() {
            this.loading = true;
            this.error = null;
            this.deleteLoading = false;
            this.saveLoading = false;
            const iteration = ++this.fetchIteration;

            var detail = null;
            if (this.orderDetailId) {
                detail = await ClientAPI.methods.getOrderDetailById(this.orderDetailId)
                    .catch(error => {
                        this.error = error;
                        console.error(error);
                    })
                    .finally(() => {
                        this.loading = false;
                    });
                detail.exisitngStockTracking = {
                    ArticleCount: detail.ArticleCount,
                    WareHouseId: detail.WareHouseId,
                    StoreLocationId: detail.StoreLocationId
                }
            }
            else {
                const dto = {
                    ArticleNumber: this.$route.params.articleNumber,
                    CustomerId: this.customerId,
                    JobId: this.childJobId,
                    MachineId: this.machineId
                }
                detail = await ClientAPI.methods.generateOrderDetailDraft(dto);
                detail.exisitngStockTracking = {
                    ArticleCount: 0,
                    WareHouseId: null,
                    StoreLocationId: null
                }
            }
            //ignore if a newer fetch has been started
            if (this.fetchIteration === iteration) {
                detail.shouldSendVendorEmail = false;
                detail.WarehouseManagerEmailMessage = "";
                detail.SendVendorEmail = undefined;
                this.editObject = Object.assign({}, detail);
                this.loading = false;
            }
        },

        async save() {
            this.saveLoading = true;
            const payload = Object.assign({}, this.editObject);
            //if SendVendorEmail not set, set from shouldSendVendorEmail as default
            if (payload.SendVendorEmail === undefined) {
                payload.SendVendorEmail = payload.shouldSendVendorEmail;
            }
            //SendWarehouseEmailToAddress: this.sendWareHouseEmail ? this.sendWareHouseEmailTo : null,
            //SendVendorOrderEmailToAddress: this.sendWareHouseEmail ? this.sendWareHouseEmailTo : null

            var apiMethod = this.isNew ? this.createFunc : this.updateFunc;
            const articleNumber = this.$route.params.articleNumber;

            await apiMethod(payload)
                .then(data => {
                    if (this.isNew) {
                        //if still on the same new article, go to edit that line
                        if (this.$route.params.articleNumber === articleNumber) {
                            this.$router.push({
                                name: 'JobEditOrderLine', params: {
                                    filter: this.$route.params.filter,
                                    jobId: this.$route.params.jobId,
                                    machineJobId: this.$route.params.machineJobId,
                                    lineId: data.Id
                                }
                            });
                        }
                    }
                })
                .finally(() => {
                    this.saveLoading = false;
                });
        },

        async remove() {
            if (!await this.$store.dispatch('ConfirmDialog/Show')) {
                return;
            }
            const lineId = this.$route.params.lineId;
            this.deleteLoading = true;
            const payload = Object.assign({}, this.editObject);
            await this.deleteFunc(payload)
                .then(() => {
                    //if still on the same line, close the editor
                    if (this.$route.params.lineId === lineId) {
                        this.close();
                    }
                })
                .finally(() => {
                    this.deleteLoading = false;
                });
        },

        getDurationHoursFloat(start, end) {
            const startTime = moment(start);
            const endTime = moment(end);
            const duration = moment.duration(endTime.diff(startTime));
            const totalHours = duration.asHours();
            return totalHours;
        },
    },

    computed: {
        isNew() {
            return !this.editObject || !this.editObject.Id;
        },

        jobLocked() {
            return !this.JobDetails || this.JobDetails.JobStatusId > 69;
        },

        JobDetails() {
            return this.$store.getters["JobHub/JobDetails"];
        },

        locked() {
            //console.log('locked', this.editObject.ReadOnly, this.jobLocked)
            return this.editObject.ReadOnly || this.jobLocked;
        },

        anyLoading() {
            return this.loading || this.saveLoading || this.deleteLoading;
        },

        childJobId() {
            return this.$route.params.machineJobId;
        },
        subjob() {
            var childjobs = this.$store.getters["JobHub/Subjobs"];
            return childjobs?.find(x => x.Id == this.subjobId);
        },
        machineId() {
            return this.subjob?.Machine?.Id
        },
        customerId() {
            return (this.$store.getters["JobHub/JobCustomer/Customer"])?.Id;
        },

        orderDetailId() {
            return this.$route.params.lineId;
        },

        showHourEditor() {
            var type = this.editObject?.Type;
            var key = TimeTypeConstants.values[type]
            return type && TimeTypeConstants[key]?.inHours;
        },

        timeTypeChip() {
            var key = TimeTypeConstants.values[this.editObject?.Type]
            var type = TimeTypeConstants[key];
            if (type) {
                return {
                    text: this.$t(type.display),
                    icon: type.icon
                };
            }
            else {
                return null;
            }
        },

        isPart() {
            return !this.editObject?.Type || this.editObject?.Type === TimeTypeConstants.Unknown.value;
        },

        timeControls: {
            get() {
                return {
                    Date: this.editObject.EmploymentTimeReportDate,
                    Start: this.editObject.StartDateTime,
                    End: this.editObject.StopDateTime,
                    Duration: this.getDurationHoursFloat(this.editObject.StartDateTime, this.editObject.StopDateTime),
                    Count: this.editObject.ArticleCount,
                    AdjustCount: this.editObject.ArticleCount === this.getDurationHoursFloat(this.editObject.StartDateTime, this.editObject.StopDateTime),
                };
            },
            set(value) {
                //console.log("timeControls set", value);
                this.editObject.EmploymentTimeReportDate = value.Date;
                this.editObject.StartDateTime = value.Start;
                this.editObject.StopDateTime = value.End;
                this.editObject.ArticleCount = value.Count;
                this.articleCountKey++;
            }
        },

        stockControls: {
            get() {
                return {
                    Id: this.editObject.Id,
                    exisitngStockTracking: this.editObject.exisitngStockTracking,
                    ArticleCount: this.editObject.ArticleCount,
                    StoreId: this.editObject.WareHouseId,
                    StoreLocationId: this.editObject.StoreLocationId,
                    DisplayName: this.editObject.WareHouseLocation,
                    SendVendorEmail: this.editObject.SendVendorEmail,
                    WarehouseManagerEmailMessage: this.editObject.WarehouseManagerEmailMessage,
                };
            },
            set(value) {
                this.editObject.WareHouseId = value.StoreId;
                this.editObject.StoreLocationId = value.StoreLocationId;
                this.editObject.WareHouseLocation = value.DisplayName;
                this.editObject.SendVendorEmail = value.SendVendorEmail;
                this.editObject.shouldSendVendorEmail = value.shouldSendVendorEmail;
                this.editObject.WarehouseManagerEmailMessage = value.WarehouseManagerEmailMessage;
                console.log("stockControls set", value);
            }
        },

        priceControls: {
            get() {
                return {
                    ArticleCount: this.editObject.ArticleCount,
                    ArticlePrice: this.editObject.ArticlePrice,
                    Discount: this.editObject.Discount,
                    Duration: this.getDurationHoursFloat(this.editObject.StartDateTime, this.editObject.StopDateTime),
                };
            },
            set(value) {
                //console.log("priceControls set", value);
                this.editObject.ArticleCount = value.ArticleCount;
                this.editObject.ArticlePrice = value.ArticlePrice;
                this.editObject.Discount = value.Discount;
                this.timeControlkey++;
            }
        },

        headerText() {
            if (this.loading) {
                return this.$t("servicesystem.common_objects.fallbackterms.loading");
            }
            else if (this.editObject?.Id) {
                return this.$t("servicesystem.common_objects.commonterms.edit_object", { okey: this.timeTypeChip?.text });
            }
            else {
                return this.$t("servicesystem.common_objects.commonterms.add_article");
            }
        },
    },

    watch: {
        "$route.params.lineId": {
            handler: function () {
                this.load();
            }
        },
    }
}
</script>

<style scoped>
.minitoolbar {
    position: sticky;
    top: 0px;
    z-index: 10;
    /*box-shadow: 0px 1px 5px var(--v-header-darken2) !important;*/
}
</style>
